import React, { useState, useRef, useEffect } from 'react';
import { aiApi } from '../../services/api';
import { ChatMessage } from '../../types';
import { PaperAirplaneIcon } from 'components/icons';
import ReactMarkdown from 'react-markdown';
import PageHeader from '../../components/ui/PageHeader';

const AIAssistant: React.FC = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: 'welcome',
      role: 'assistant',
      content: 'Hello! I\'m your GovCon AI Assistant. I can help you with:\n\n• Finding and analyzing contract opportunities\n• Writing proposal sections\n• Understanding FAR/DFARS regulations\n• Compliance questions\n• Win strategy development\n\nWhat would you like help with today?',
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: input,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await aiApi.chat({
        message: input,
        history: messages.map((m) => ({ role: m.role, content: m.content })),
      });

      const assistantMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: response.data.response,
        citations: response.data.citations,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Chat error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col">
      <div className="mb-4">
        <PageHeader
          title="GovCon AI Assistant"
          subtitle="Ask about a solicitation and get answers grounded in its actual documents."
        />
      </div>

      {/* Chat Messages */}
      <div className="flex-1 overflow-y-auto bg-surface rounded-lg shadow border border-border p-4 space-y-4">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-3xl rounded-lg px-4 py-3 ${
                message.role === 'user'
                  ? 'bg-brand-600 text-white'
                  : 'bg-surface-sunk text-content'
              }`}
            >
              {message.role === 'assistant' ? (
                <div className="markdown prose prose-sm max-w-none">
                  <ReactMarkdown>{message.content}</ReactMarkdown>
                </div>
              ) : (
                <p>{message.content}</p>
              )}
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-surface-sunk rounded-lg px-4 py-3 flex items-center space-x-2">
              <div className="animate-bounce h-2 w-2 bg-gray-400 rounded-full"></div>
              <div className="animate-bounce h-2 w-2 bg-gray-400 rounded-full" style={{ animationDelay: '0.1s' }}></div>
              <div className="animate-bounce h-2 w-2 bg-gray-400 rounded-full" style={{ animationDelay: '0.2s' }}></div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="mt-4 flex gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSend()}
          placeholder="Ask about RFPs, compliance, proposal writing..."
          className="flex-1 input"
          disabled={isLoading}
        />
        <button
          onClick={handleSend}
          disabled={isLoading || !input.trim()}
          className="btn-primary flex items-center gap-2 disabled:opacity-50"
        >
          <PaperAirplaneIcon className="h-5 w-5" />
          Send
        </button>
      </div>

      {/* Suggested Prompts */}
      <div className="mt-4 flex flex-wrap gap-2">
        {[
          'What are CMMC Level 2 requirements?',
          'Help me write a technical approach',
          'Explain FAR Part 15',
          'Analyze proposal risks',
        ].map((prompt) => (
          <button
            key={prompt}
            onClick={() => setInput(prompt)}
            className="text-xs bg-surface-sunk hover:bg-gray-200 px-3 py-1.5 rounded-full text-content transition-colors"
          >
            {prompt}
          </button>
        ))}
      </div>
    </div>
  );
};

export default AIAssistant;
